import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ColorLineConstants } from './../../../../../shared/colorline-constant';
import { ComponentCodeConstants } from './../../../../../shared/component-constants';

import { CommonModule } from '@angular/common';
import { AvailabilityComponent } from '@app/shared/components/availability/availability-text.component';
import { MenuOverlayComponent } from '@app/shared/components/menu-overlay/menu-overlay';
import { VariantColorFromApiModel } from '@models/variant-color-from-api';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTranslatePipe } from '@pipes/data-translate.pipe';
import { BrandModeService } from '@services/brandmode.service';
import { LanguageService } from '@services/language.service';
import { ColorModel } from '@models/color';
import { VariantColorModel } from '@models/variant-color';
import { MiscModel } from '../../../../../_models/misc-model';
import { ImageService } from '../../../../../_services/image.service';
import { SunDetailsStore } from '../../../../shared/stores/sun-details.store';

@Component({
	selector: 'customizer-color-item',
	templateUrl: 'customizer-color-item.html',
	styleUrls: ['./customizer-color-item.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, DataTranslatePipe, TranslatePipe, AvailabilityComponent, MenuOverlayComponent]
})
export class CustomizerColorItemComponent implements OnInit {

	@Input() editMode: boolean;
	@Input() selected?: boolean | null;
	@Input() color: VariantColorFromApiModel | null;
	@Input() showNoneChoiceOption: boolean = true;
	@Input() extendedInformation: boolean;
	@Input() extendedInformationTable?: boolean = true;
	@Input() noneChoiceTranslation: string;
	@Input() specialColorText: string;
	@Input() imageClass: string;
	@Input() matchableColors: Array<ColorModel | null>;
	@Input() matchableMisc: Array<MiscModel>;
	@Input() useColorGroup: boolean = true;
	@Input() variantOrModelIsNew: boolean = false;
	@Input() matchColor: number;
	@Output() clickedChange: EventEmitter<void> = new EventEmitter();
	@Output() matchColorClicked: EventEmitter<VariantColorFromApiModel | null> = new EventEmitter();
	@Output() matchMiscClicked: EventEmitter<MiscModel> = new EventEmitter();

	public showDetailId: number | null;
	public showOverlay: boolean = false;
	public mainOverlayText: string;
	public secondOverlayText: string;
	public matchedMiscModel?: MiscModel | null;
	public imageHeight: number = 20;

	constructor(
		public imageService: ImageService,
		public translateService: LanguageService,
		public brandModeService: BrandModeService
	) { }

	ngOnInit() {
		this.setImageHeight();
	}

	private setImageHeight() {
		if (this.color) {
			if (this.color.Color?.ColorLine?.Code == ColorLineConstants.TWIRE ||
				this.color.Color?.ColorLine?.Code == ColorLineConstants.GROOVE) {
				this.imageHeight = 20;
			} else if (this.color.ComponentCode == ComponentCodeConstants.Lenses) {
				this.imageHeight = 100;
			} else if (this.color.ComponentCode == ComponentCodeConstants.Front && this.brandModeService.isPrecious) {
				this.imageHeight = 100;
			} else if (this.color.ComponentCode == ComponentCodeConstants.Innerrim && this.brandModeService.isPrecious) {
				this.imageHeight = 100;
			} else if (this.color.ComponentCode == ComponentCodeConstants.Cover) {
				this.imageHeight = 35;

			} else {
				this.imageHeight = 16;
			}
		}
	}

	public hasVariantsOfSameColorFunction(color: VariantColorModel | null): boolean | null {
		if (!this.matchableColors) {
			return null;
		}

		if (this.matchableColors && color && color.Color) {
			return this.matchableColors.some(c => c?.Code == color.Color?.Code);
		}

		return false;
	}

	public hasMiscOfSameColorFunction(color: VariantColorModel | null): MiscModel | null | undefined {
		if (!this.matchableMisc || !color || !color.Color) {
			return null;
		} else {
			return this.matchableMisc.find(misc => misc.FirstSplitEcommerceNoUnique == color.Color?.FirstSplitColor ||
				misc.FirstSplitEcommerceNoUnique == color.Color?.LastSplitColor ||
				misc.LastSplitEcommerceNoUnique == color.Color?.FirstSplitColor ||
				misc.LastSplitEcommerceNoUnique == color.Color?.LastSplitColor);
		}
	}

	showDetails(id: number) {
		if (this.showDetailId === id) {
			this.showDetailId = null;
		} else {
			this.showDetailId = id;
		}
	}

	onClicked() {
		this.clickedChange.emit();

		const hasVariantsOfSameColor = this.hasVariantsOfSameColorFunction(this.color);
		this.matchedMiscModel = this.hasMiscOfSameColorFunction(this.color);

		if (hasVariantsOfSameColor) {
			if (this.matchColor === 1) {
				this.matchColorClicked.emit(this.color);
			}
			else if (this.matchColor === 2) {
				this.showOverlay = true;
				this.mainOverlayText = this.translateService.instant('CUSTOMIZER.COLOR.MATCHCOLOR');

				new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
					this.showOverlay = false;
				});
			}
		}
		else if (this.matchedMiscModel) {
			if (this.matchColor === 1) {
				this.matchMiscClicked.emit(this.matchedMiscModel);
			}
			else if (this.matchColor === 2) {
				this.showOverlay = true;
				this.mainOverlayText = this.translateService.instant('CUSTOMIZER.COLOR.MATCHGROOVECOLOR');

				new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
					this.showOverlay = false;
				});
			}
		}

		if (!hasVariantsOfSameColor && !this.matchedMiscModel && this.selected && this.matchColor > 1) { // doubleclick
			this.showOverlay = true;
			this.secondOverlayText = this.translateService.instant('CUSTOMIZER.COLOR.NOMATCHCOLOR');

			new Promise(resolve => setTimeout(resolve, 1500)).then(x => {
				this.showOverlay = false;
			});
		}
		else if (this.editMode) {
			this.showOverlay = true;
			this.secondOverlayText = this.translateService.instant('CUSTOMIZER.CHANGES.BAGUPDATED');

			new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
				this.showOverlay = false;
			});
		}
	}

	onColorMatchClicked() {
		if (this.matchedMiscModel) {
			this.matchMiscClicked.emit(this.matchedMiscModel);
		} else {
			this.matchColorClicked.emit(this.color);
		}

		this.showOverlay = false;
	}

	sunDetailsStore = inject(SunDetailsStore);

	showSunDetails($event: any) {
		$event.stopPropagation();

		this.sunDetailsStore.setShowSunDetails(true);
	}

}
